import { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Button, Space, Tooltip, message, Switch } from 'antd';
import {
  SendOutlined, ThunderboltOutlined,
  LoadingOutlined, FileOutlined, CloseOutlined, BulbOutlined,
  ApartmentOutlined, ThunderboltOutlined as WorkflowOutlined,
} from '@ant-design/icons';
import { useChatStore, UploadedFile } from '@/stores/chat';
import AttachMenu from './AttachMenu';

const { TextArea } = Input;

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_TYPES = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx',
  '.ppt', '.pptx', '.txt', '.md', '.csv', '.json', '.xml',
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg',
  '.py', '.js', '.ts', '.jsx', '.tsx', '.java', '.go', '.rs',
  '.c', '.cpp', '.h', '.cs', '.rb', '.php',
  '.mp3', '.wav', '.ogg', '.mp4', '.webm',
];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  onSend: (text: string) => void;
  onPromptOptimize: (text: string) => void;
  disabled?: boolean;
  autoOptimize?: boolean;
  onToggleAutoOptimize?: (next: boolean) => void;
  /** 今日免费额度已用尽（发送禁用，但不阻塞浏览） */
  exhausted?: boolean;
  /** 打开「创建智能体」弹窗 */
  onOpenCreateAgent?: () => void;
}

export default function ChatInput({ onSend, onPromptOptimize, disabled, autoOptimize, onToggleAutoOptimize, exhausted, onOpenCreateAgent }: Props) {
  const [input, setInput] = useState('');
  const { files, addFile, removeFile, clearFiles, attachedSkills, removeAttachedSkill, attachedWorkflow, setAttachedWorkflow } = useChatStore();
  const textareaRef = useRef<any>(null);

  // 自适应高度
  useEffect(() => {
    const el = textareaRef.current?.resizableTextArea?.textArea;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }
  }, [input]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || disabled || exhausted) return;
    onSend(text);
    setInput('');
    clearFiles();
  }, [input, disabled, exhausted, onSend, clearFiles]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
          setInput((prev) => prev + '\n');
        } else {
          handleSend();
        }
      }
    },
    [handleSend]
  );

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (!file) return;
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_TYPES.includes(ext)) {
        message.warning(`不支持的文件格式: ${ext}`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        message.warning(`文件过大（最大 ${formatSize(MAX_FILE_SIZE)}）`);
        return;
      }
      const f: UploadedFile = {
        id: `file_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
        status: 'uploading',
      };
      addFile(f);

      // 模拟上传进度
      let progress = 0;
      const timer = setInterval(() => {
        progress += Math.random() * 30;
        if (progress >= 100) {
          progress = 100;
          clearInterval(timer);
          useChatStore.getState().updateFile(f.id, { progress: 100, status: 'done' });
        } else {
          useChatStore.getState().updateFile(f.id, { progress: Math.round(progress) });
        }
      }, 200);
    },
    [addFile]
  );

  const hasContent = input.trim().length > 0;
  const sendDisabled = !hasContent || disabled || exhausted;
  const placeholder = exhausted
    ? '今日免费对话次数已用完，升级套餐 / 登录后可继续'
    : disabled
      ? 'AI 正在回复中...'
      : '输入消息，Enter 发送，Shift+Enter 换行';

  return (
    <div className="chat-input-area">
      {/* 已附加技能 / 关联工作流（Codex 附件菜单设置） */}
      {(attachedSkills.length > 0 || attachedWorkflow) && (
        <div className="attach-chip-area">
          <Space size={4} wrap>
            {attachedSkills.map((sk) => (
              <span key={sk.id} className="attach-chip skill">
                <ApartmentOutlined />
                <span className="chip-name">{sk.name}</span>
                <CloseOutlined style={{ fontSize: 10, cursor: 'pointer' }} onClick={() => removeAttachedSkill(sk.id)} />
              </span>
            ))}
            {attachedWorkflow && (
              <span className="attach-chip workflow">
                <WorkflowOutlined />
                <span className="chip-name">{attachedWorkflow.name}</span>
                <CloseOutlined style={{ fontSize: 10, cursor: 'pointer' }} onClick={() => setAttachedWorkflow(null)} />
              </span>
            )}
          </Space>
        </div>
      )}

      {/* 文件预览 */}
      {files.length > 0 && (
        <div className="file-preview-area">
          <Space size={4} wrap>
            {files.map((f) => (
              <div key={f.id} className="file-tag">
                <FileOutlined />
                <span className="file-name">{f.name}</span>
                <span className="file-size">{formatSize(f.size)}</span>
                {f.status === 'uploading' ? (
                  <LoadingOutlined spin style={{ fontSize: 11 }} />
                ) : (
                  <CloseOutlined
                    style={{ fontSize: 11, cursor: 'pointer' }}
                    onClick={() => removeFile(f.id)}
                  />
                )}
              </div>
            ))}
          </Space>
        </div>
      )}

      {/* Codex 风格 Composer */}
      <div className={`composer${exhausted ? ' composer-exhausted' : ''}`}>
        {/* 左侧工具栏 */}
        <div className="composer-tools">
          {/* Codex 附件菜单：上传文件/文件夹 · 对话模式 · 技能 · 智能体 · 智能工作流 */}
          <Tooltip title="附件（上传文件/文件夹、模式、技能、智能体、工作流）">
            <span className={`attach-wrap${disabled || exhausted ? ' disabled' : ''}`}>
              <AttachMenu
                onFilesSelected={(fl) => { if (!disabled && !exhausted) fl.forEach((file) => handleFileChange(file)); }}
                onOpenCreateAgent={onOpenCreateAgent}
              />
            </span>
          </Tooltip>
          <Tooltip title="AI 优化提示词">
            <Button
              type="text"
              icon={<ThunderboltOutlined />}
              disabled={!hasContent || disabled || exhausted}
              onClick={() => onPromptOptimize(input.trim())}
              className="composer-tool-btn"
            />
          </Tooltip>
          <Tooltip title={autoOptimize ? '已开启：发送前自动优化提示词' : '关闭：发送前自动优化提示词'}>
            <span className={`auto-opt-chip${autoOptimize ? ' on' : ''}`} onClick={() => !disabled && !exhausted && onToggleAutoOptimize?.(!autoOptimize)}>
              <BulbOutlined />
              <span>自动优化</span>
            </span>
          </Tooltip>
        </div>

        {/* 输入框 */}
        <TextArea
          ref={textareaRef as any}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={disabled || exhausted}
          variant="borderless"
          autoSize={false}
          style={{
            flex: 1,
            resize: 'none',
            fontSize: 14,
            lineHeight: 1.6,
            maxHeight: 160,
            minHeight: 24,
            border: 'none',
            boxShadow: 'none',
            padding: '8px 6px',
            color: 'var(--text-primary)',
          }}
        />

        {/* 发送按钮 */}
        <Button
          type="primary"
          icon={disabled ? <LoadingOutlined /> : <SendOutlined />}
          onClick={handleSend}
          disabled={sendDisabled}
          className="composer-send"
        />
      </div>

      {/* 底部提示 */}
      <div className="input-hint">
        <Space size={16}>
          <span>📎 支持 PDF/Word/Excel/图片/代码（≤50MB）</span>
          <span>⌨️ Enter 发送 · Shift+Enter 换行</span>
        </Space>
      </div>

      <style>{`
        .chat-input-area {
          border-top: 1px solid var(--border); padding: 10px 16px 8px;
          background: var(--bg-container);
        }
        .file-preview-area { margin-bottom: 8px; }
        .file-tag {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 9px; background: rgba(99, 102, 241, 0.08); border-radius: 7px;
          font-size: 12px; border: 1px solid rgba(99, 102, 241, 0.25); color: var(--text-primary);
        }
        .file-name { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .file-size { color: var(--text-tertiary); font-size: 11px; }

        .composer {
          display: flex; align-items: flex-end; gap: 4px;
          background: var(--bg-elevated);
          border: 1px solid var(--border); border-radius: 14px;
          padding: 4px 6px; transition: border-color 0.2s, box-shadow 0.2s;
        }
        .composer:focus-within {
          border-color: rgba(99, 102, 241, 0.6);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
        }
        .composer-exhausted { opacity: 0.85; }
        .composer-tools { display: flex; align-items: center; gap: 2px; padding-bottom: 2px; }
        .attach-wrap { display: inline-flex; align-items: center; color: var(--text-tertiary); cursor: pointer; }
        .attach-wrap.disabled { opacity: 0.5; pointer-events: none; }
        .attach-chip-area { margin-bottom: 6px; }
        .attach-chip {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 9px; border-radius: 7px; font-size: 12px;
        }
        .attach-chip.skill { background: rgba(139, 92, 246, 0.10); border: 1px solid rgba(139, 92, 246, 0.3); color: #8b5cf6; }
        .attach-chip.workflow { background: rgba(16, 185, 129, 0.10); border: 1px solid rgba(16, 185, 129, 0.3); color: #10b981; }
        .attach-chip .chip-name { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .composer-tool-btn { color: var(--text-tertiary); }
        .auto-opt-chip {
          display: inline-flex; align-items: center; gap: 4px; margin: 0 2px 4px;
          font-size: 11px; color: var(--text-tertiary); cursor: pointer;
          padding: 2px 8px; border-radius: 20px; border: 1px solid var(--border);
          background: transparent; user-select: none; transition: all 0.2s;
        }
        .auto-opt-chip.on {
          color: #fff; background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-color: transparent;
        }
        .composer-send {
          width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0;
          background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .composer-send:disabled { background: rgba(148, 163, 184, 0.25); }
        .input-hint {
          display: flex; justify-content: center; margin-top: 4px;
          font-size: 11px; color: var(--text-tertiary);
        }
        @media (max-width: 768px) { .input-hint { display: none; } .auto-opt-chip span:last-child { display: none; } }
      `}</style>
    </div>
  );
}
